import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './SocialPostReaction.css';
// Audio pour le son de like
const LIKE_SOUND_URL = 'https://peupleqc.com/s/peupleqc-upload/sound/pop-39222.mp3';
import { 
  FaThumbsUp, 
  FaRegThumbsUp, 
  FaShare, 
  FaRegComment, 
  FaPaperPlane,
  FaRegSmile,
  FaAngleDown,
  FaAngleUp,
  FaEye
} from "react-icons/fa";
import { SocialUserAvatar } from "./SocialUserAvatar";
import { SocialUserName } from "./SocialUserName";
import { NewSocialPostForm } from "./NewSocialPostForm";
import { PostLikesList } from "./PostLikesList";
import { useNavigate } from "./Router";

const SocialPostReaction = ({ post }) => {
  const navigate = useNavigate();
  const [hasLiked, setHasLiked] = useState(post.hasLiked);
  const [comments, setComments] = useState(post.comments || []);
  const [showComments, setShowComments] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [sharedFromId, setSharedFromId] = useState(null);
  const [user, setUser] = useState(null);
  const [nbrLikes, setNbrLikes] = useState(post.nbrLikes || 0);
  const [nbrShares, setNbrShares] = useState(post.nbrShare || 0);
  const [nbrComments, setNbrComments] = useState(comments.length || 0);
  const [isLikeAnimating, setIsLikeAnimating] = useState(false);
  const [commentLikeAnimations, setCommentLikeAnimations] = useState({});
  const [showLikesList, setShowLikesList] = useState(false);
  const commentInputRef = useRef(null);
  const likeAudioRef = useRef(null);

  const toggleModal = () => setModalOpen(!modalOpen);

  const toggleComments = () => {
    setShowComments(!showComments);
    
    // Si on ouvre les commentaires, focus sur l'input
    if (!showComments) {
      setTimeout(() => {
        if (commentInputRef.current) {
          commentInputRef.current.focus();
        }
      }, 100);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("liberium-token");
    if (token) {
      const userData = localStorage.getItem("myself");
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          console.error("Erreur lors de la lecture des données utilisateur:", e);
        }
      }
    }
  }, []);

  // Fonction pour transformer le texte avec les liens cliquables
  const processTextWithLinks = (text) => {
    if (!text) return null;
    
    // Regex pour détecter les URLs (améliorée)
    const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;
    
    // Division du texte en fragments (texte et liens)
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Recherche tous les liens dans le texte
    while ((match = urlRegex.exec(text)) !== null) {
      // Ajoute le texte avant le lien
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // Traitement de l'URL trouvée
      const url = match[0];
      // Nettoyer l'URL de la ponctuation finale
      const cleanUrl = url.replace(/[.,!?;:]$/, '');
      
      if (cleanUrl.startsWith('https://peupleqc.com/') || cleanUrl.startsWith('http://peupleqc.com/')) {
        // Lien interne - naviguer avec le router
        const internalPath = cleanUrl.replace(/^https?:\/\/peupleqc\.com/, '');
        parts.push({
          type: 'internal-link',
          url: cleanUrl,
          path: internalPath,
          displayText: cleanUrl.length > 35 ? cleanUrl.substring(0, 32) + '...' : cleanUrl
        });
      } else {
        // Lien externe - ouvrir dans un nouvel onglet
        parts.push({
          type: 'external-link',
          url: cleanUrl,
          displayText: cleanUrl.length > 35 ? cleanUrl.substring(0, 32) + '...' : cleanUrl
        });
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Ajoute le reste du texte après le dernier lien
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    // Si aucun lien n'a été trouvé, retourner le texte original
    if (parts.length === 0) {
      return text;
    }
    
    // Rendu avec les parties
    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={index}>{part.content}</span>;
      } else if (part.type === 'internal-link') {
        return (
          <span 
            key={index}
            className="spr-comment-link spr-internal-link"
            onClick={() => navigate(part.path)}
            title={part.url}
          >
            {part.displayText}
          </span>
        );
      } else if (part.type === 'external-link') {
        return (
          <a
            key={index}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className="spr-comment-link spr-external-link"
            title={part.url}
          >
            {part.displayText}
          </a>
        );
      }
      return null;
    });
  };
  
  const handleLike = async () => {
    try {
      // Optimistic UI update
      const isNowLiked = !hasLiked;
      setHasLiked(isNowLiked);
      setNbrLikes(isNowLiked ? nbrLikes + 1 : nbrLikes - 1);
      setIsLikeAnimating(true);
      
      // Jouer le son si l'utilisateur like
      if (isNowLiked) {
        playLikeSound();
      }
      
      // Appel à l'API
      await sdk.models.SocialPostLike.create({
        post_id: post.id,
        user_id: user.user_id,
        status: isNowLiked ? 1 : 0
      });
      
    } catch (error) {
      console.error("Erreur lors de la mise à jour du like:", error);
      // Revenir à l'état précédent en cas d'erreur
      setHasLiked(!isNowLiked);
      setNbrLikes(hasLiked ? nbrLikes - 1 : nbrLikes + 1);
    } finally {
      // Désactiver l'animation après un délai
      setTimeout(() => {
        setIsLikeAnimating(false);
      }, 500);
    }
  };
  
  // Gestion des likes sur les commentaires
  const handleCommentLike = async (comment, index) => {
    try {
      // Mise à jour optimiste UI
      const isNowLiked = !comment.hasLiked;
      
      // Animation
      setCommentLikeAnimations({
        ...commentLikeAnimations,
        [comment.id]: true
      });
      
      // Mise à jour des données
      const updatedComments = [...comments];
      const updatedComment = { ...comment };
      updatedComment.hasLiked = isNowLiked;
      updatedComment.likeCount = (updatedComment.likeCount || 0) + (isNowLiked ? 1 : -1);
      updatedComments[index] = updatedComment;
      setComments(updatedComments);
      
      // Appel API
      await sdk.models.SocialCommentLike.create({
        comment_id: comment.id,
        user_id: user.user_id,
        status: isNowLiked ? 1 : 0
      });
      
    } catch (error) {
      console.error("Erreur lors du like de commentaire:", error);
      // Restaurer l'état précédent
      const updatedComments = [...comments];
      const updatedComment = { ...comment };
      updatedComment.likeCount = (updatedComment.likeCount || 0) + (updatedComment.hasLiked ? 1 : -1);
      updatedComments[index] = updatedComment;
      setComments(updatedComments);
    } finally {
      // Fin de l'animation après délai
      setTimeout(() => {
        setCommentLikeAnimations({
          ...commentLikeAnimations,
          [comment.id]: false
        });
      }, 500);
    }
  };

  const handleShare = () => {
    setSharedFromId(post.id);
    toggleModal();
  };

  const handleShareSuccess = () => {
    setNbrShares(nbrShares + 1);
    toggleModal();
  };
  
  const handleAddComment = async () => {
    if (newComment.trim() === "") {
      return;
    }
    
    setIsSubmitting(true);
    setError("");
    
    try {
      const response = await sdk.models.SocialComment.create({
        post_id: post.id,
        user_id: user.user_id,
        text: newComment,
        time: Math.floor(Date.now() / 1000),
      });
      
      // Ajouter le nouveau commentaire au début (il est le plus récent)
      setComments([response, ...comments]);
      setNbrComments(nbrComments + 1);
      setNewComment("");
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", error);
      setError("Une erreur s'est produite lors de l'ajout du commentaire");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effet pour initialiser l'audio
  useEffect(() => {
    likeAudioRef.current = new Audio(LIKE_SOUND_URL);
    likeAudioRef.current.volume = 0.5; // Volume à 50%
  }, []);
  
  // Fonction pour jouer le son de like
  const playLikeSound = () => {
    if (likeAudioRef.current) {
      likeAudioRef.current.currentTime = 0; // Réinitialise l'audio pour pouvoir être rejoué immédiatement
      likeAudioRef.current.play().catch(err => {
        // Gestion silencieuse des erreurs de lecture audio (navigateurs qui bloquent l'autoplay)
        console.log("Lecture audio non autorisée:", err);
      });
    }
  };
  
  // Si l'utilisateur n'est pas connecté, ne pas afficher les réactions
  if (!user) return null;

  // Déterminer quels commentaires afficher
  const displayedComments = showAllComments 
    ? comments 
    : comments.slice(0, 2);

  const hasMoreComments = comments.length > 2;

  return (
    <div className="spr-container">
      {/* Compteurs des interactions */}
      {(nbrLikes > 0 || nbrComments > 0 || nbrShares > 0 || post.views) && (
        <div className="spr-stats">
          {nbrLikes > 0 && (
            <div 
              className="spr-stat-item spr-stat-button" 
              onClick={() => setShowLikesList(true)}
            >
              <FaThumbsUp className="spr-icon spr-like-icon" />
              <span>{nbrLikes}</span>
            </div>
          )}
          <div className="spr-stat-spacer"></div>
          <div className="spr-stat-right">
            {post.views && (
              <div className="spr-stat-item">
                <FaEye className="spr-icon spr-view-icon" />
                <span>{post.views}</span>
              </div>
            )}
            {nbrComments > 0 && (
              <div className="spr-stat-button" onClick={toggleComments}>
                {nbrComments} commentaire{nbrComments > 1 ? 's' : ''}
              </div>
            )}
            {nbrShares > 0 && (
              <span className="spr-stat-item">
                {nbrShares} partage{nbrShares > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Boutons d'interaction */}
      <div className="spr-buttons">
        <div 
          className={`spr-button spr-like-button ${hasLiked ? 'spr-active' : ''} ${isLikeAnimating ? 'spr-animating' : ''}`}
          onClick={handleLike}
        >
          {hasLiked ? (
            <FaThumbsUp className="spr-button-icon" />
          ) : (
            <FaRegThumbsUp className="spr-button-icon" />
          )}
          <span className="spr-button-text">J'aime</span>
        </div>
        
        <div 
          className={`spr-button ${showComments ? 'spr-active' : ''}`}
          onClick={toggleComments}
        >
          <FaRegComment className="spr-button-icon" />
          <span className="spr-button-text">Commenter</span>
        </div>
        
        <div 
          className="spr-button"
          onClick={handleShare}
        >
          <FaShare className="spr-button-icon" />
          <span className="spr-button-text">Partager</span>
        </div>
      </div>

      {/* Affichage des commentaires récents (jusqu'à 2) même si la section commentaires n'est pas ouverte */}
      {nbrComments > 0 && displayedComments.length > 0 && (
        <div className="spr-recent-comments">
          {displayedComments.map((comment, index) => (
            <div key={comment.id || index} className="spr-comment-item">
              <SocialUserAvatar 
                user_id={comment.user_id} 
                className="spr-comment-user-avatar" 
              />
              <div className="spr-comment-content">
                <div className="spr-comment-bubble">
                  <SocialUserName 
                    user_id={comment.user_id} 
                    className="spr-comment-username" 
                  />
                  <div className="spr-comment-text">
                    {processTextWithLinks(comment.text)}
                  </div>
                </div>
                <div className="spr-comment-meta">
                  <span className="spr-comment-time">
                    {comment.time ? formatCommentTime(comment.time) : "À l'instant"}
                  </span>
                  <div 
                    className={`spr-comment-like ${comment.hasLiked ? 'spr-active' : ''} ${commentLikeAnimations[comment.id] ? 'spr-animating' : ''}`}
                    onClick={() => handleCommentLike(comment, index)}
                  >
                    {comment.hasLiked ? "J'aime" : "J'aime"}
                    {(comment.likeCount > 0) && ` · ${comment.likeCount}`}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Bouton "Voir plus de commentaires" ou "Voir moins de commentaires" */}
          {hasMoreComments && (
            <div 
              className="spr-toggle-comments" 
              onClick={() => setShowAllComments(!showAllComments)}
            >
              {showAllComments ? (
                <>
                  <FaAngleUp className="spr-toggle-icon" />
                  <span>Voir moins de commentaires</span>
                </>
              ) : (
                <>
                  <FaAngleDown className="spr-toggle-icon" />
                  <span>Voir plus de commentaires ({comments.length - 2})</span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Section des commentaires (formulaire de commentaire) */}
      {showComments && (
        <div className="spr-comments-section">
          {/* Zone de saisie du commentaire */}
          <div className="spr-comment-input-container">
            <SocialUserAvatar 
              user_id={user.user_id} 
              className="spr-comment-avatar" 
            />
            <div className="spr-input-wrapper">
              <input
                type="text"
                ref={commentInputRef}
                className="spr-comment-input"
                placeholder="Écrire un commentaire..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
                disabled={isSubmitting}
              />
              <div className="spr-input-actions">
                <div 
                  className="spr-emoji-button"
                  title="Insérer un emoji"
                >
                  <FaRegSmile />
                </div>
                <div 
                  className={`spr-submit-button ${isSubmitting || newComment.trim() === "" ? 'spr-disabled' : ''}`}
                  onClick={isSubmitting || newComment.trim() === "" ? null : handleAddComment}
                >
                  <FaPaperPlane />
                </div>
              </div>
            </div>
          </div>
          
          {error && <p className="spr-comment-error">{error}</p>}
        </div>
      )}

      {/* Modal de partage */}
      <NewSocialPostForm
        user={user}
        sharedFromId={sharedFromId}
        isOpen={modalOpen}
        toggleModal={toggleModal}
        setNewPost={() => handleShareSuccess()}
      />

      {/* Modal Liste des likes */}
      <PostLikesList 
        postId={post.id}
        isOpen={showLikesList}
        onClose={() => setShowLikesList(false)}
      />
    </div>
  );
};

// Fonction utilitaire pour formater le temps des commentaires
const formatCommentTime = (timestamp) => {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "À l'instant";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `Il y a ${minutes} mn`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `Il y a ${hours}h`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `Il y a ${days}j`;
  } else {
    return date.toLocaleDateString();
  }
};

export { SocialPostReaction };